import { useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchActiveCountries } from '@/shared/api/places';
import { FINE_STATUS, FINE_STATUS_OPTIONS } from '@/shared/constants/bids';
import { formatCost, formatUnixDateTime, inputValueToUnix } from '@/shared/lib/datetime';
import { toOptions } from '@/shared/lib/options';
import { getListRows } from '@/shared/lib/pagination';
import { DateTimeInput } from '@/shared/ui/DateTimeInput';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { Pagination } from '@/shared/ui/Pagination';
import { fetchFinesList, type FinesListParams } from './api';

interface FineRow extends Record<string, unknown> {
  id: number | string;
  create_time?: number;
  request_time?: number;
  status_id?: number;
  service?: { id?: number | string; name?: string } | null;
  bid?: { id?: number | string; cost_work?: number | string } | null;
  fine_cost?: { total_sum?: number | string } | null;
  service_balance?: { type_id?: number; balance?: number | string } | null;
}

const DEFAULT_COUNTRY_ID = '1';

const emptyFilters = {
  serviceId: '',
  serviceName: '',
  bidId: '',
  statusId: '',
  dateFrom: '',
  dateTo: '',
  countryId: DEFAULT_COUNTRY_ID,
};

type Filters = typeof emptyFilters;

const labelStyle: CSSProperties = { display: 'block', fontWeight: 600, marginBottom: 4 };

const getPagesCount = (data: unknown): number => {
  const pagination = (data as { pagination?: { pageSize?: number; totalCount?: number } } | undefined)?.pagination;
  const pageSize = Number(pagination?.pageSize);
  const totalCount = Number(pagination?.totalCount);
  return pageSize > 0 && totalCount > 0 ? Math.ceil(totalCount / pageSize) : 1;
};

export const FinesPage = () => {
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState<Filters>({ ...emptyFilters });
  const [applied, setApplied] = useState<Filters>({ ...emptyFilters });

  const countriesQuery = useQuery({ queryKey: ['active-countries'], queryFn: fetchActiveCountries });

  const listParams = useMemo<FinesListParams>(
    () => ({
      page,
      service_id: applied.serviceId,
      name: applied.serviceName,
      bid_id: applied.bidId,
      status_id: applied.statusId,
      create_time_from: inputValueToUnix(applied.dateFrom),
      create_time_to: inputValueToUnix(applied.dateTo),
      country_id: applied.countryId,
    }),
    [applied, page],
  );

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['fines', listParams],
    queryFn: () => fetchFinesList(listParams),
  });

  const rows = getListRows<FineRow>(data, ['fines']);

  const setDraftField = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    setPage(1);
    setApplied({ ...draft });
  };

  const onReset = (e: FormEvent) => {
    e.preventDefault();
    const next = { ...emptyFilters, countryId: '' };
    setDraft(next);
    setApplied(next);
    setPage(1);
  };

  return (
    <>
      <section className="content-header">
        <h1>Штрафы</h1>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-header with-border">
            <form onSubmit={onSearch}>
              <div className="row">
                <div className="col-md-3">
                  <div className="form-group">
                    <span style={labelStyle}>Поиск</span>
                    <input
                      className="form-control input-sm"
                      style={{ width: 250, marginBottom: 6 }}
                      placeholder="Поиск по ID сервиса..."
                      value={draft.serviceId}
                      onChange={(e) => setDraftField('serviceId', e.target.value)}
                    />
                    <input
                      className="form-control input-sm"
                      style={{ width: 250, marginBottom: 6 }}
                      placeholder="Поиск по имени сервиса..."
                      value={draft.serviceName}
                      onChange={(e) => setDraftField('serviceName', e.target.value)}
                    />
                    <input
                      className="form-control input-sm"
                      style={{ width: 250 }}
                      placeholder="Поиск по ID заявки..."
                      value={draft.bidId}
                      onChange={(e) => setDraftField('bidId', e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <span style={labelStyle}>Начиная с даты</span>
                    <DateTimeInput
                      type="date"
                      value={draft.dateFrom}
                      max={draft.dateTo || undefined}
                      onChange={(v) => setDraftField('dateFrom', v)}
                    />
                  </div>
                  <div className="form-group">
                    <span style={labelStyle}>Заканчивая датой</span>
                    <DateTimeInput
                      type="date"
                      value={draft.dateTo}
                      min={draft.dateFrom || undefined}
                      onChange={(v) => setDraftField('dateTo', v)}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <span style={labelStyle}>Статус</span>
                    <FilterSelect
                      options={FINE_STATUS_OPTIONS}
                      value={draft.statusId}
                      onChange={(v) => setDraftField('statusId', v)}
                    />
                  </div>
                  <div className="form-group">
                    <span style={labelStyle}>Страна</span>
                    <FilterSelect
                      options={toOptions(countriesQuery.data)}
                      value={draft.countryId}
                      isLoading={countriesQuery.isLoading}
                      onChange={(v) => setDraftField('countryId', v)}
                    />
                  </div>
                </div>
              </div>
              <button type="button" className="btn btn-warning" onClick={onReset} disabled={isFetching}>
                Очистить фильтры
              </button>{' '}
              <button type="submit" className="btn btn-primary" disabled={isFetching}>
                Найти
              </button>
            </form>
          </div>

          <div className="box-body">
            {isLoading && <p>Загрузка…</p>}
            {error && <div className="alert alert-danger">Ошибка загрузки штрафов</div>}
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Дата и время начисления</th>
                    <th>Дата и время обжалования</th>
                    <th>ID СТО</th>
                    <th>Название СТО</th>
                    <th>Номер заявки</th>
                    <th>Размер штрафа</th>
                    <th>Размер стоимости ремонта</th>
                    <th>Тип кошелька при списании штрафа</th>
                    <th>Баланс кошелька после списания</th>
                    <th>Статус заявки</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((fine) => {
                    const serviceId = fine.service?.id ?? '';
                    const bidId = fine.bid?.id ?? '';

                    return (
                      <tr key={fine.id}>
                        <td>{formatUnixDateTime(fine.create_time)}</td>
                        <td>{formatUnixDateTime(fine.request_time)}</td>
                        <td>
                          <Link to={`/users/user/${serviceId}`}>{serviceId}</Link>
                        </td>
                        <td>
                          <Link to={`/users/user/${serviceId}`}>{fine.service?.name ?? ''}</Link>
                        </td>
                        <td>
                          <Link to={`/bids/bid/${bidId}`}>{bidId}</Link>
                        </td>
                        <td>{formatCost(fine.fine_cost?.total_sum)} ₽</td>
                        <td>{formatCost(fine.bid?.cost_work)} ₽</td>
                        <td>{fine.service_balance?.type_id === 0 ? 'Основной' : 'Бонусный'}</td>
                        <td>{formatCost(fine.service_balance?.balance)} ₽</td>
                        <td>{FINE_STATUS[fine.status_id ?? -1] ?? ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={getPagesCount(data)} onChange={setPage} />
          </div>
        </div>
      </section>
    </>
  );
};
